// LinearPlacementTool — click two points to place a row of objects along a line
// Auto-computes count from product width, allows count adjustment via confirmation panel

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { getViewer, getSelectedProduct, getSelectedProductLayer, getToolMode, setSelectedProduct, stopContinuousPlacement } from '@/ui/state'
import { debug } from '@/utils/debug'
import { t } from '@/i18n'

export interface LinearPlacementConfig {
  overlay: DimensionOverlay
  /** Called to commit all objects. Returns array of created IDs. */
  onCommit: (objects: Array<{
    type: 'building' | 'plant'
    productId: string
    x: number
    y: number
    width: number
    height: number
    rotation: number
    elevation: number
    productName: string
  }>) => string[]
}

type Phase = 'idle' | 'drawing' | 'confirming'

export class LinearPlacementTool implements Tool {
  readonly name = 'linear-placement'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.linearPlacement'

  private toolManager: ToolManager
  private config: LinearPlacementConfig

  private phase: Phase = 'idle'
  private startPoint: { x: number; y: number } | null = null
  private endPoint: { x: number; y: number } | null = null
  private currentWorld: THREE.Vector3 | null = null
  private lastCamera: THREE.Camera | null = null
  private count = 2

  // Preview
  private previewGroup: THREE.Group
  private panelEl: HTMLElement | null = null

  constructor(toolManager: ToolManager, scene: THREE.Scene, config: LinearPlacementConfig) {
    this.toolManager = toolManager
    this.config = config

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'linear-placement-preview'
    scene.add(this.previewGroup)
  }

  activate(): void {
    this.phase = 'idle'
    this.startPoint = null
    this.endPoint = null
    this.currentWorld = null
    this.count = 2
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('LinearPlacementTool', 'Activated')
  }

  deactivate(): void {
    this.cancelAll()
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('LinearPlacementTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.editorPoint || !ctx.worldPoint) return
    if (this.phase === 'confirming') return // Clicks during confirmation are ignored

    if (this.phase === 'idle') {
      // First click — set start point
      this.startPoint = { x: ctx.editorPoint.x, y: ctx.editorPoint.y }
      this.phase = 'drawing'
      this.updatePreview()
      ctx.event.preventDefault()
      ctx.event.stopPropagation()
      debug.log('LinearPlacementTool', `Start point: (${ctx.editorPoint.x}, ${ctx.editorPoint.y})`)
    } else if (this.phase === 'drawing') {
      // Second click — set end point, enter confirmation
      this.endPoint = { x: ctx.editorPoint.x, y: ctx.editorPoint.y }
      this.phase = 'confirming'
      this.autoComputeCount()
      this.updatePreview()
      this.showConfirmationPanel()
      ctx.event.preventDefault()
      ctx.event.stopPropagation()
      debug.log('LinearPlacementTool', `End point: (${ctx.editorPoint.x}, ${ctx.editorPoint.y}), count: ${this.count}`)
    }
  }

  onMouseMove(ctx: ToolContext): void {
    if (!ctx.worldPoint) return
    this.currentWorld = ctx.worldPoint.clone()
    this.lastCamera = ctx.camera
    this.toolManager.setCursor(this.cursor)

    if (this.phase === 'drawing' && this.startPoint) {
      this.updatePreview()
    }
  }

  onMouseUp(_ctx: ToolContext): void {
    // Click handling in onMouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelAll()
      stopContinuousPlacement()
      this.toolManager.setActiveTool('select')
    }
  }

  dispose(): void {
    this.cancelAll()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private getProductWidth(): number {
    const product = getSelectedProduct()
    return product?.width ?? 50 // default 1m in dots
  }

  private autoComputeCount(): void {
    if (!this.startPoint || !this.endPoint) return
    const dx = this.endPoint.x - this.startPoint.x
    const dy = this.endPoint.y - this.startPoint.y
    const lineLength = Math.sqrt(dx * dx + dy * dy)
    const productWidth = this.getProductWidth()
    this.count = Math.max(2, Math.min(100, Math.round(lineLength / (productWidth * 0.95)) + 1))
  }

  private computePositions(): Array<{ x: number; y: number }> {
    const start = this.startPoint
    const end = this.phase === 'confirming'
      ? this.endPoint
      : (this.currentWorld
        ? { x: this.currentWorld.x * DPM, y: this.currentWorld.z * DPM }
        : null)

    if (!start || !end) return []

    const positions: Array<{ x: number; y: number }> = []
    const count = this.phase === 'confirming' ? this.count : this.previewCount(start, end)

    for (let i = 0; i < count; i++) {
      const t = count <= 1 ? 0 : i / (count - 1)
      positions.push({
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t,
      })
    }
    return positions
  }

  private previewCount(start: { x: number; y: number }, end: { x: number; y: number }): number {
    const dx = end.x - start.x
    const dy = end.y - start.y
    const lineLength = Math.sqrt(dx * dx + dy * dy)
    const productWidth = this.getProductWidth()
    return Math.max(2, Math.min(100, Math.round(lineLength / (productWidth * 0.95)) + 1))
  }

  private computeRotation(): number {
    const start = this.startPoint
    const end = this.phase === 'confirming'
      ? this.endPoint
      : (this.currentWorld
        ? { x: this.currentWorld.x * DPM, y: this.currentWorld.z * DPM }
        : null)
    if (!start || !end) return 0
    const dx = end.x - start.x
    const dy = end.y - start.y
    // atan2(dy, dx) gives 0° for horizontal lines (no rotation needed)
    return Math.atan2(dy, dx) * (180 / Math.PI)
  }

  private getLineLength(): number {
    const start = this.startPoint
    const end = this.phase === 'confirming'
      ? this.endPoint
      : (this.currentWorld
        ? { x: this.currentWorld.x * DPM, y: this.currentWorld.z * DPM }
        : null)
    if (!start || !end) return 0
    const dx = end.x - start.x
    const dy = end.y - start.y
    return Math.sqrt(dx * dx + dy * dy) / DPM // meters
  }

  private updatePreview(): void {
    this.clearPreview()

    const positions = this.computePositions()
    if (positions.length === 0) return

    const y = 0.02
    const color = 0x4ecca3

    // Draw line between start and end
    const start = this.startPoint!
    const end = this.phase === 'confirming'
      ? this.endPoint!
      : (this.currentWorld
        ? { x: this.currentWorld.x * DPM, y: this.currentWorld.z * DPM }
        : start)

    const linePoints = [
      new THREE.Vector3(start.x / DPM, y, start.y / DPM),
      new THREE.Vector3(end.x / DPM, y, end.y / DPM),
    ]
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(linePoints)
    const lineMaterial = new THREE.LineDashedMaterial({
      color,
      transparent: true,
      opacity: 0.6,
      dashSize: 0.15,
      gapSize: 0.1,
    })
    const line = new THREE.Line(lineGeometry, lineMaterial)
    line.computeLineDistances()
    this.previewGroup.add(line)

    // Ghost circles at each position
    const circleGeometry = new THREE.CircleGeometry(this.getProductWidth() / DPM / 2, 16)
    const circleMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    for (const pos of positions) {
      const circle = new THREE.Mesh(circleGeometry, circleMaterial)
      circle.rotation.x = -Math.PI / 2
      circle.position.set(pos.x / DPM, y + 0.01, pos.y / DPM)
      this.previewGroup.add(circle)
    }

    // Dot centers
    const dotGeometry = new THREE.SphereGeometry(0.06, 8, 8)
    const dotMaterial = new THREE.MeshBasicMaterial({ color })
    for (const pos of positions) {
      const dot = new THREE.Mesh(dotGeometry, dotMaterial)
      dot.position.set(pos.x / DPM, y + 0.05, pos.y / DPM)
      this.previewGroup.add(dot)
    }

    // Start anchor sphere (larger)
    const anchorGeometry = new THREE.SphereGeometry(0.1, 12, 12)
    const anchorMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff })
    const anchor = new THREE.Mesh(anchorGeometry, anchorMaterial)
    anchor.position.set(start.x / DPM, y + 0.05, start.y / DPM)
    this.previewGroup.add(anchor)

    // Show dimension overlay
    if (this.config.overlay && this.lastCamera) {
      const length = this.getLineLength()
      const midX = (start.x / DPM + end.x / DPM) / 2
      const midZ = (start.y / DPM + end.y / DPM) / 2
      this.config.overlay.showEdge(length, midX, midZ, this.lastCamera)
    }
  }

  private clearPreview(): void {
    while (this.previewGroup.children.length > 0) {
      const child = this.previewGroup.children[0]
      this.previewGroup.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      } else if (child instanceof THREE.Line) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      }
    }
  }

  private showConfirmationPanel(): void {
    this.removeConfirmationPanel()

    const panel = document.createElement('div')
    panel.className = 'linear-confirm-panel'
    panel.innerHTML = `
      <div class="lcp-row">
        <label class="lcp-label">${t('placement.count', 'Count')}</label>
        <div class="lcp-stepper">
          <button class="lcp-btn" data-action="dec">−</button>
          <input type="number" class="lcp-input" value="${this.count}" min="2" max="100" />
          <button class="lcp-btn" data-action="inc">+</button>
        </div>
      </div>
      <div class="lcp-row">
        <span class="lcp-label">${t('placement.spacing', 'Spacing')}</span>
        <span class="lcp-value" data-field="spacing">${this.formatSpacing()}</span>
      </div>
      <div class="lcp-row">
        <span class="lcp-label">${t('placement.length', 'Length')}</span>
        <span class="lcp-value" data-field="length">${this.getLineLength().toFixed(2)} m</span>
      </div>
      <button class="lcp-confirm">${t('placement.confirm', 'Confirm')}</button>
    `

    // Style the panel
    Object.assign(panel.style, {
      position: 'fixed',
      bottom: '60px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(30, 33, 40, 0.92)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      padding: '12px 16px',
      color: '#fff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '13px',
      zIndex: '1100',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minWidth: '220px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.08)',
    })

    document.body.appendChild(panel)
    this.panelEl = panel

    // Wire events
    const input = panel.querySelector('.lcp-input') as HTMLInputElement
    const decBtn = panel.querySelector('[data-action="dec"]') as HTMLButtonElement
    const incBtn = panel.querySelector('[data-action="inc"]') as HTMLButtonElement
    const confirmBtn = panel.querySelector('.lcp-confirm') as HTMLButtonElement

    input.addEventListener('change', () => {
      this.count = Math.max(2, Math.min(100, parseInt(input.value, 10) || 2))
      input.value = String(this.count)
      this.onCountChange()
    })

    decBtn.addEventListener('click', () => {
      if (this.count > 2) {
        this.count--
        input.value = String(this.count)
        this.onCountChange()
      }
    })

    incBtn.addEventListener('click', () => {
      if (this.count < 100) {
        this.count++
        input.value = String(this.count)
        this.onCountChange()
      }
    })

    confirmBtn.addEventListener('click', () => {
      this.commitPlacement()
    })

    // Style inner elements
    this.stylePanel(panel)
  }

  private stylePanel(panel: HTMLElement): void {
    panel.querySelectorAll<HTMLElement>('.lcp-row').forEach(row => {
      Object.assign(row.style, {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
      })
    })

    panel.querySelectorAll<HTMLElement>('.lcp-label').forEach(el => {
      Object.assign(el.style, {
        color: 'rgba(255,255,255,0.6)',
        fontSize: '12px',
      })
    })

    panel.querySelectorAll<HTMLElement>('.lcp-value').forEach(el => {
      Object.assign(el.style, {
        fontWeight: '500',
        fontSize: '12px',
      })
    })

    const stepper = panel.querySelector('.lcp-stepper') as HTMLElement
    if (stepper) {
      Object.assign(stepper.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      })
    }

    panel.querySelectorAll<HTMLElement>('.lcp-btn').forEach(btn => {
      Object.assign(btn.style, {
        width: '26px',
        height: '26px',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        background: 'rgba(255,255,255,0.06)',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
      })
    })

    const input = panel.querySelector('.lcp-input') as HTMLElement
    if (input) {
      Object.assign(input.style, {
        width: '44px',
        height: '26px',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        background: 'rgba(255,255,255,0.06)',
        color: '#fff',
        textAlign: 'center',
        fontSize: '13px',
        padding: '0',
        outline: 'none',
      })
    }

    const confirmBtn = panel.querySelector('.lcp-confirm') as HTMLElement
    if (confirmBtn) {
      Object.assign(confirmBtn.style, {
        marginTop: '4px',
        padding: '8px 16px',
        border: 'none',
        borderRadius: '8px',
        background: '#4ecca3',
        color: '#1a1a2e',
        fontWeight: '600',
        fontSize: '13px',
        cursor: 'pointer',
      })
    }
  }

  private onCountChange(): void {
    this.updatePreview()
    // Update spacing readout in panel
    if (this.panelEl) {
      const spacingEl = this.panelEl.querySelector('[data-field="spacing"]')
      if (spacingEl) spacingEl.textContent = this.formatSpacing()
    }
  }

  private formatSpacing(): string {
    if (this.count <= 1) return '—'
    const length = this.getLineLength()
    const spacing = length / (this.count - 1)
    return `${spacing.toFixed(2)} m`
  }

  private commitPlacement(): void {
    if (!this.startPoint || !this.endPoint) return

    const product = getSelectedProduct()
    if (!product) {
      debug.warn('LinearPlacementTool', 'No product selected')
      return
    }

    const layer = getSelectedProductLayer()
    const objectType: 'building' | 'plant' = layer === 'architecture' ? 'building' : 'plant'
    const rotation = this.computeRotation()
    const positions = this.computePositions()

    const objects = positions.map(pos => ({
      type: objectType,
      productId: product.id,
      x: Math.round(pos.x),
      y: Math.round(pos.y),
      width: product.width ?? 50,
      height: product.height ?? 50,
      rotation: Math.round(rotation),
      elevation: product.elevation ?? 0,
      productName: product.name,
    }))

    // Ensure product is cached
    getViewer()?.ensureProductCached(product)

    const ids = this.config.onCommit(objects)
    debug.log('LinearPlacementTool', `Committed ${ids.length} objects`)

    // Clean up and return to select
    this.cancelAll()
    setSelectedProduct(null)
    this.toolManager.setActiveTool('select')
  }

  private removeConfirmationPanel(): void {
    if (this.panelEl) {
      this.panelEl.remove()
      this.panelEl = null
    }
  }

  private cancelAll(): void {
    this.phase = 'idle'
    this.startPoint = null
    this.endPoint = null
    this.currentWorld = null
    this.lastCamera = null
    this.count = 2
    this.clearPreview()
    this.removeConfirmationPanel()
    this.config.overlay?.hideAll()
  }
}
